import { OPTIONS } from '../core/Settings.js';

/**
 * The settings overlay.
 *
 * Rows are built from the option schema rather than written into the markup, so
 * adding an option is a one-line change in `Settings.js`. Each row owns its own
 * control and re-reads the store on every change, which keeps the panel honest
 * if something else flips a setting (the wheel controls do, for instance).
 */
export class SettingsPanel {
  constructor({ settings, onClose }) {
    this.settings = settings;
    this.onClose = onClose;

    this.root = document.getElementById('settings');
    this.rows = document.getElementById('settings-rows');
    this.closeButton = document.getElementById('settings-close');
    this.isOpen = false;

    /** option id -> { button, state } for every rendered toggle. */
    this.controls = new Map();

    this.#build();

    this.closeButton?.addEventListener('click', () => this.close());
    this.root?.addEventListener('pointerdown', (event) => {
      // A press on the backdrop closes; a press inside the card does not.
      if (event.target === this.root) this.close();
    });

    this._onKeyDown = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    };
    // Capture phase, so Escape closes the panel instead of pausing the game.
    window.addEventListener('keydown', this._onKeyDown, true);

    // Keep the panel in sync with changes made elsewhere.
    this._unsubscribe = this.settings.onChange((id) => this.#paint(id));
  }

  #build() {
    if (!this.rows) return;
    // textContent works on every WebView the game supports; replaceChildren()
    // only arrived in Chromium 86 and is missing on older Android tablets.
    this.rows.textContent = '';

    for (const option of OPTIONS) {
      const row = document.createElement('div');
      row.className = 'settings-row';

      const copy = document.createElement('div');
      copy.className = 'settings-copy';

      const label = document.createElement('span');
      label.className = 'settings-label';
      label.id = `settings-label-${option.id}`;
      label.textContent = option.label;

      const hint = document.createElement('span');
      hint.className = 'settings-hint';
      hint.textContent = option.hint;

      copy.appendChild(label);
      copy.appendChild(hint);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'settings-switch';
      button.setAttribute('role', 'switch');
      button.setAttribute('aria-labelledby', label.id);
      button.dataset.option = option.id;

      const track = document.createElement('span');
      track.className = 'settings-switch-track';
      const knob = document.createElement('span');
      knob.className = 'settings-switch-knob';
      track.appendChild(knob);

      const state = document.createElement('span');
      state.className = 'settings-switch-state';

      button.appendChild(track);
      button.appendChild(state);
      button.addEventListener('click', () => this.settings.toggle(option.id));

      row.appendChild(copy);
      row.appendChild(button);
      this.rows.appendChild(row);
      this.controls.set(option.id, { button, state });
    }

    for (const option of OPTIONS) this.#paint(option.id);
  }

  #paint(id) {
    const control = this.controls.get(id);
    if (!control) return;

    const value = this.settings.get(id) === true;
    control.button.setAttribute('aria-checked', String(value));
    control.button.classList.toggle('is-on', value);
    control.state.textContent = value ? 'On' : 'Off';
  }

  open() {
    this.isOpen = true;
    if (this.root) {
      this.root.inert = false;
      this.root.setAttribute('aria-hidden', 'false');
      this.root.classList.add('visible');
    }
    this.closeButton?.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (this.root) {
      this.root.classList.remove('visible');
      this.root.setAttribute('aria-hidden', 'true');
      this.root.inert = true;
    }
    this.onClose?.();
  }

  dispose() {
    window.removeEventListener('keydown', this._onKeyDown, true);
    this._unsubscribe?.();
  }
}
